import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type LlmMetadataSettings = {
  enabled: boolean;
  jsonld_enabled: boolean;
  llms_enabled: boolean;
  app_name: string;
  app_description: string;
  app_category: string;
  features: string[];
  llms_summary: string;
  llms_citation: string;
};

const DEFAULT_LLM_METADATA: LlmMetadataSettings = {
  enabled: true,
  jsonld_enabled: true,
  llms_enabled: true,
  app_name: 'CineRig Pro',
  app_description: 'Build camera rigs with compatibility checks, power planning, and signal-flow clarity.',
  app_category: 'MultimediaApplication',
  features: [
    'Camera rig building and component management',
    'Compatibility checking across mounts/ports/power',
    'Signal flow and power flow visualization',
    'Exportable build summaries',
  ],
  llms_summary:
    'CineRig Pro helps film crews plan camera rigs with compatibility checks, power planning, and clear signal-flow documentation.',
  llms_citation: 'CineRig Pro (cinerig.pro) — accessed {date}',
};

export function useLlmMetadata() {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<LlmMetadataSettings>(DEFAULT_LLM_METADATA);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('global_settings')
          .select('setting_value')
          .eq('setting_key', 'llm_metadata')
          .maybeSingle();

        if (error) throw error;

        const value = (data?.setting_value ?? null) as any;
        if (!cancelled && value) {
          setSettings({
            ...DEFAULT_LLM_METADATA,
            ...value,
            features: Array.isArray(value.features) ? (value.features as string[]) : DEFAULT_LLM_METADATA.features,
          });
        }
      } catch {
        // Swallow errors silently; this is best-effort and should never block rendering.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isJsonLdEnabled = useMemo(() => settings.enabled && settings.jsonld_enabled, [settings]);
  const isLlmsEnabled = useMemo(() => settings.enabled && settings.llms_enabled, [settings]);

  return { settings, isLoading, isJsonLdEnabled, isLlmsEnabled };
}
